import * as React from 'react'
import {
  Box,
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  FormGroup,
} from '@mui/material'
import Modal from '../../FormCreationModal'
import { fetchAllTenants } from '../../../services/tenantService'
import { assignTenantToUser } from '../../../services/userTenantService'
import { ASSIGNMENT_ACCESS_MODE, TREE_PERMISSION } from '../../../utils/treePermissions'
import { PERMISSION_OPTIONS } from './columns'

const DEFAULT_PERMISSIONS = Object.freeze({
  [TREE_PERMISSION.REGISTER]: true,
  [TREE_PERMISSION.UPDATE]: true,
})

const ACCESS_MODE_NOTICES = {
  [ASSIGNMENT_ACCESS_MODE.READ_ONLY]:
    'Visitante: solo lectura (ve la ficha completa de este tenant, no registra ni actualiza).',
  [ASSIGNMENT_ACCESS_MODE.TOTAL]:
    'Administrador: acceso total; los permisos por asignación no aplican.',
  [ASSIGNMENT_ACCESS_MODE.NONE]: 'Este rol no tiene permisos de escritura.',
}

const AssignModal = ({ userId, assignedTenantIds, accessMode, onSuccess }, ref) => {
  const [open, setOpen] = React.useState(false)
  const [allTenants, setAllTenants] = React.useState([])
  const [selectedTenant, setSelectedTenant] = React.useState(null)
  const [isPrimary, setIsPrimary] = React.useState(false)
  const [permissions, setPermissions] = React.useState(DEFAULT_PERMISSIONS)
  const [loading, setLoading] = React.useState(false)

  const isFlagsMode = accessMode === ASSIGNMENT_ACCESS_MODE.FLAGS

  React.useImperativeHandle(ref, () => ({ openModal: () => setOpen(true) }))

  React.useEffect(() => {
    if (!open) return
    fetchAllTenants().then(tenants => setAllTenants(tenants))
  }, [open])

  const availableTenants = React.useMemo(() => {
    const assignedSet = new Set(assignedTenantIds || [])
    return allTenants.filter(t => !assignedSet.has(t.id))
  }, [allTenants, assignedTenantIds])

  const handleClose = () => {
    setOpen(false)
    setSelectedTenant(null)
    setIsPrimary(false)
    setPermissions(DEFAULT_PERMISSIONS)
  }

  const handlePermissionChange = permission => event =>
    setPermissions(current => ({ ...current, [permission]: event.target.checked }))

  const handleAssign = async () => {
    if (!selectedTenant || !userId) return
    setLoading(true)
    // Los flags solo se envían en modo FLAGS; si se omiten, la API aplica su default o conserva los guardados.
    const result = await assignTenantToUser(
      userId,
      selectedTenant.id,
      isPrimary,
      isFlagsMode ? permissions : {},
    )
    setLoading(false)
    if (result.success) {
      handleClose()
      if (onSuccess) onSuccess(result.message || 'Tenant asignado correctamente')
    } else {
      if (onSuccess) onSuccess(result.message, false)
    }
  }

  return (
    <Modal open={open} title="Asignar Tenant" description="Selecciona un tenant para asignar al usuario">
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Autocomplete
          options={availableTenants}
          getOptionLabel={option => option.fullName || option.name || ''}
          value={selectedTenant}
          onChange={(_, value) => setSelectedTenant(value)}
          renderInput={params => (
            <TextField {...params} label="Tenant" variant="outlined" size="small" />
          )}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          noOptionsText="No hay tenants disponibles"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isPrimary}
              onChange={e => setIsPrimary(e.target.checked)}
              size="small"
            />
          }
          label="Es principal"
        />

        {isFlagsMode ? (
          <FormGroup>
            {PERMISSION_OPTIONS.map(({ permission, label }) => (
              <FormControlLabel
                key={permission}
                control={
                  <Checkbox
                    checked={permissions[permission]}
                    onChange={handlePermissionChange(permission)}
                    size="small"
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
        ) : (
          <Alert severity="info">{ACCESS_MODE_NOTICES[accessMode]}</Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={!selectedTenant || loading}
          >
            {loading ? 'Asignando...' : 'Asignar'}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default React.forwardRef(AssignModal)
